import { useEffect, useState } from 'react';
import type { ProductSelectOption } from '@/components/products';
import { apiFetch } from '@/lib/api';
import { parseProductSelectOptions } from '@/lib/productCatalog';

export interface UseProductCatalogOptions {
  /** When true, loads only ACTIVE products suitable for operational forms. */
  activeForOps?: boolean;
  pageSize?: number;
  /** When false, skips fetching entirely (for mode-dependent catalogs). */
  enabled?: boolean;
}

export function useProductCatalog(options: UseProductCatalogOptions = {}) {
  const { activeForOps = false, pageSize = 500, enabled = true } = options;
  const [products, setProducts] = useState<ProductSelectOption[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setProducts([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ pageSize: String(pageSize) });
        if (activeForOps) params.set('activeForOps', 'true');

        const res = await apiFetch(`/api/v1/products?${params}`);
        if (!res.ok) {
          throw new Error('Failed to load products');
        }
        const data = await res.json();
        if (!cancelled) {
          setProducts(parseProductSelectOptions(data));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load products');
          setProducts([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [activeForOps, pageSize, enabled]);

  return { products, loading, error };
}
